import React from 'react';
import { Progress } from 'antd';
import { AddItemFormData, PricingOption, LABEL_MAPS, getLabel } from './types';

interface ReviewStepProps {
  styles: { [key: string]: string };
  formData: AddItemFormData;
  isWearable: boolean;
  isSkill?: boolean;
  isWorkflow?: boolean;
  pricingOptions: PricingOption[];
}

const ReviewStep: React.FC<ReviewStepProps> = ({ styles, formData, isWearable, isSkill, isWorkflow, pricingOptions }) => (
  <div className={styles.stepContainer}>
    <h2 className={styles.stepTitle} style={{marginBottom : '5px'}}>Review and Submit</h2>
    
    <div className={styles.reviewSection}>
      <h3 className={styles.reviewTitle}>Basic Details</h3>
      <div className={styles.reviewItem}>
        <span className={styles.reviewLabel}>Item Name</span>
        <span className={styles.reviewValue}>{formData.itemName}</span>
      </div>
      
      <div className={styles.reviewItem}>
        <span className={styles.reviewLabel}>Item Type</span>
        <span className={styles.reviewValue}>{getLabel(formData.itemType, LABEL_MAPS.itemType)}</span>
      </div>
      
      {!isWearable && (
        <>
          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Industry</span>
            <span className={styles.reviewValue}>{getLabel(formData.industry, LABEL_MAPS.industry)}</span>
          </div>

          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Language</span>
            <span className={styles.reviewValue}>{getLabel(formData.language, LABEL_MAPS.language)}</span>
          </div>

          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Type</span>
            <span className={styles.reviewValue}>{isSkill || isWorkflow ? formData.type : getLabel(formData.type, LABEL_MAPS.type)}</span>
          </div>
        </>
      )}
      
      <div className={styles.reviewItem}>
        <span className={styles.reviewLabel}>Tags</span>
        <span className={styles.reviewValue}>{formData.tags}</span>
      </div>
      
      <div className={styles.reviewItem}>
        <span className={styles.reviewLabel}>Description</span>
        <span className={styles.reviewValue}>{formData.description}</span>
      </div>

      {isWearable && (
        <>
          <h3 className={styles.reviewTitle}>Wearable Details</h3>
          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Wearable Type</span>
            <span className={styles.reviewValue}>{formData.wearableType}</span>
          </div>
          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Material</span>
            <span className={styles.reviewValue}>{formData.material}</span>
          </div>
          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Style</span>
            <span className={styles.reviewValue}>{formData.style}</span>
          </div>
          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Fabric</span>
            <span className={styles.reviewValue}>{formData.fabric}</span>
          </div>
        </>
      )}

      {isWorkflow && (
        <>
          <h3 className={styles.reviewTitle}>Workflow Details</h3>
          {formData.version && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Version</span>
              <span className={styles.reviewValue}>{formData.version}</span>
            </div>
          )}
          {formData.encryptionStandard && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Encryption Standard</span>
              <span className={styles.reviewValue}>{formData.encryptionStandard}</span>
            </div>
          )}
          {formData.encriptionAtributes && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Encryption Attributes</span>
              <span className={styles.reviewValue}>{formData.encriptionAtributes}</span>
            </div>
          )}
          {formData.marketplaceId && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Marketplace ID</span>
              <span className={styles.reviewValue}>{formData.marketplaceId}</span>
            </div>
          )}
          {formData.lastVerifiedAt && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Last Verified At</span>
              <span className={styles.reviewValue}>{formData.lastVerifiedAt}</span>
            </div>
          )}
          {formData.lastVerifiedBy && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Last Verified By</span>
              <span className={styles.reviewValue}>{formData.lastVerifiedBy}</span>
            </div>
          )}
          {formData.note && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Note</span>
              <span className={styles.reviewValue}>{formData.note}</span>
            </div>
          )}
          {formData.content && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Content</span>
              <span className={styles.reviewValue}>{formData.content}</span>
            </div>
          )}
        </>
      )}

      {isSkill && (
        <>
          <h3 className={styles.reviewTitle}>Skill Details</h3>
          {formData.version && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Version</span>
              <span className={styles.reviewValue}>{formData.version}</span>
            </div>
          )}
          {formData.marketplaceId && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Marketplace ID</span>
              <span className={styles.reviewValue}>{formData.marketplaceId}</span>
            </div>
          )}
          {formData.lastVerifiedAt && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Last Verified At</span>
              <span className={styles.reviewValue}>{formData.lastVerifiedAt}</span>
            </div>
          )}
          {formData.lastVerifiedBy && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Last Verified By</span>
              <span className={styles.reviewValue}>{formData.lastVerifiedBy}</span>
            </div>
          )}
          {formData.note && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Note</span>
              <span className={styles.reviewValue}>{formData.note}</span>
            </div>
          )}
          {formData.content && (
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Content</span>
              <span className={styles.reviewValue}>{formData.content}</span>
            </div>
          )}
        </>
      )}

      {!isWearable && (
        <>
          <h3 className={styles.reviewTitle}>License Information</h3>
          <section className={styles.licenseReviewSection}>
          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Terms of Use</span>
            <span className={styles.reviewValue}>{formData.terms_of_use}</span>
          </div>

          <div className={styles.reviewItem}>
            <span className={styles.reviewLabel}>Privacy Policy</span>
            <span className={styles.reviewValue}>{formData.privacy_policy}</span>
          </div>
          </section>
        </>
      )}

      <h3 className={styles.reviewTitle}>Pricing Details</h3>
    
      <section className={styles.pricingReviewSection}>
      
      {formData.pricingModel.map(model => {
        const opt = pricingOptions.find(o => o.value === model);
        return (
          <div key={model} className={styles.reviewPricingBlock}>
            <div className={styles.reviewItem}>
              <span className={styles.reviewLabel}>Pricing Model</span>
              <span className={styles.reviewValue}>{opt?.label || getLabel(model, LABEL_MAPS.pricingModel)}</span>
            </div>
            {model !== 'free' && model !== 'freemium' && formData.pricingDetails[model] && (
              <>
                <div className={styles.reviewItem}>
                  <span className={styles.reviewLabel}>Price (USD)</span>
                  <span className={styles.reviewValue}>${formData.pricingDetails[model].price}</span>
                </div>
                {formData.pricingDetails[model].description && (
                  <div className={styles.reviewItem}>
                    <span className={styles.reviewLabel}>Description</span>
                    <span className={styles.reviewValue}>{formData.pricingDetails[model].description}</span>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
      
      {!isWearable && !isSkill && !isWorkflow && formData.itemType !== 'avatar' && (
        <div className={styles.reviewItem}>
          <span className={styles.reviewLabel}>Supported Interfaces</span>
          <span className={styles.reviewValue}>
            {formData.supportedInterfaces.map(iface => getLabel(iface, LABEL_MAPS.interface)).join(', ')}
          </span>
        </div>
      )}
      </section>
      
    </div>

    {formData.thumbnail && formData.thumbnail.length > 0 && (
      <div className={styles.agentMedia}>
        <h3 className={styles.reviewTitle}>Uploaded Media</h3>
        <div className={styles.mediaGrid}>
          {formData.iconUrl  && (
            <div className={styles.mediaItem}>
              <div className={styles.mediaPreview}>
                <img 
                  src={formData.iconUrl} 
                  alt="Thumbnail Preview"
                  className={styles.mediaImage}
                />
              </div>
              <p className={styles.mediaLabel}>Thumbnail</p>
              <p className={styles.mediaInfo}>{formData.thumbnail[0]?.name}</p>
            </div>
          )}
        </div>
      </div>
    )}

    <div className={styles.submissionStatus}>
      <h3 className={styles.reviewTitle}>Submission Status</h3>
      <div className={styles.statusItem}>
        <span className={styles.statusLabel}>Review Progress</span>
        <Progress 
          percent={100} 
          status="success" 
          className={styles.statusProgress} 
          showInfo={true} 
        />
      </div>
      <p className={styles.statusDescription}>
        Please review all the information above before final submission. You can go back to previous steps to make any necessary changes.
      </p>
    </div>
  </div>
);

export default ReviewStep;
